<script>
import { required } from 'vuelidate/lib/validators'
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import axios from 'axios'

export default {
	page: {
		title: 'OTX Pulse Management',
		meta: [{ name: 'description', content: appConfig.description }],
	},
	components: { Layout, PageHeader },
	data() {
		return {
			title: 'OTX Pulse Management',
			category: 'Management',
			items: [
				{
					text: 'Home',
					href: '/',
				},
				{
					text: 'Indicators',
					href: '/apps/indicators',
				},
				{
					text: 'OTX Pulse Management',
					active: true,
				},
			],
			appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
			
			// Pulse Management Data
			pulses: [],
			totalRows: 0,
			currentPage: 1,
			perPage: 10,
			pageOptions: [10, 25, 50, 100],
			filter: null,
			filterOn: [],
			sortBy: 'created_at',
			sortDesc: true,
			isBusy: false,
			
			// Active tab
			activeTab: 'list',
			
			// Search functionality
			searchQuery: '',
			searchResults: [],
			isSearching: false,
			
			// Add pulse form
			addPulseForm: {
				pulse_id: '',
				name: '',
				description: '',
				schedule_interval: '1 day',
				enabled: true
			},
			
			// Pulse info for preview
			pulseInfo: null,
			isLoadingPulseInfo: false,
			
			// Modals
			showAddModal: false,
			showInfoModal: false,
			showScheduleModal: false,
			showDeleteModal: false,
			
			// Selected pulse for operations
			selectedPulse: null,
			
			// Schedule form
			scheduleForm: {
				schedule_interval: '1 day'
			},
			
			// Status filter
			statusFilter: 'all',
			
			// Table fields
			fields: [
				{ key: 'pulse_id', label: 'Pulse ID', sortable: true },
				{ key: 'name', label: 'Name', sortable: true },
				{ key: 'status', label: 'Status', sortable: true },
				{ key: 'schedule.interval', label: 'Schedule', sortable: true },
				{ key: 'created_at', label: 'Created', sortable: true },
				{ key: 'actions', label: 'Actions', sortable: false }
			],
			
			// Schedule intervals
			scheduleIntervals: [
				{ value: '1 hour', text: '1 Hour' },
				{ value: '1 day', text: '1 Day' },
				{ value: '1 week', text: '1 Week' },
				{ value: '1 month', text: '1 Month' }
			],
			
			// Notifications
			notifications: []
		}
	},
	validations: {
		addPulseForm: {
			pulse_id: { required },
		},
		scheduleForm: {
			schedule_interval: { required }
		}
	},
	computed: {
		rows() {
			return this.totalRows
		},
		filteredPulses() {
			if (this.statusFilter === 'all') {
				return this.pulses
			}
			return this.pulses.filter(pulse => pulse.status === this.statusFilter)
		}
	},
	mounted() {
		this.loadPulses()
	},
	methods: {
		// API Methods
		async apiCall(method, url, data = null) {
			const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
			const config = {
				method,
				url: `${baseURL}${url}`,
				headers: {
					'Content-Type': 'application/json'
				}
			}
			
			if (data) {
				config.data = data
			}
			
			try {
				const response = await axios(config)
				return response.data
			} catch (error) {
				console.error('API Error:', error)
				this.showNotification('error', (error.response && error.response.data && error.response.data.error) || 'API request failed')
				throw error
			}
		},
		
		// Load pulses from API
		async loadPulses() {
			this.isBusy = true
			try {
				const params = new URLSearchParams({
					limit: this.perPage.toString(),
					offset: ((this.currentPage - 1) * this.perPage).toString()
				})
				
				if (this.statusFilter !== 'all') {
					params.append('status', this.statusFilter)
				}
				
				const result = await this.apiCall('GET', `/otx/pulses?${params}`)
				
				if (result.success) {
					this.pulses = result.pulses
					this.totalRows = result.total_count
				} else {
					this.showNotification('error', result.error || 'Failed to load pulses')
				}
			} catch (error) {
				this.showNotification('error', 'Failed to load pulses')
			} finally {
				this.isBusy = false
			}
		},
		
		// Search pulses in OTX
		async searchPulses() {
			if (!this.searchQuery.trim()) {
				this.showNotification('warning', 'Please enter a search query')
				return
			}
			
			this.isSearching = true
			try {
				const result = await this.apiCall('GET', `/otx/search?q=${encodeURIComponent(this.searchQuery)}`)
				
				if (result.success) {
					this.searchResults = result.search_results || []
					const count = result.total_count || this.searchResults.length || 0
					this.showNotification('success', `Found ${count} results`)
				} else {
					this.showNotification('error', result.error || 'Search failed')
				}
			} catch (error) {
				this.showNotification('error', 'Search failed')
			} finally {
				this.isSearching = false
			}
		},
		
		// Get pulse info before adding
		async getPulseInfo(pulseId) {
			this.isLoadingPulseInfo = true
			try {
				const result = await this.apiCall('GET', `/otx/pulses/${pulseId}/info`)
				
				if (result.success || result.details) {
					this.pulseInfo = result
					this.addPulseForm.name = (result.details && result.details.name) || ''
					this.addPulseForm.description = (result.details && result.details.description) || ''
				} else {
					this.showNotification('error', result.error || 'Failed to get pulse info')
					this.pulseInfo = null
				}
			} catch (error) {
				this.showNotification('error', 'Failed to get pulse info')
				this.pulseInfo = null
			} finally {
				this.isLoadingPulseInfo = false
			}
		},
		
		// Add pulse
		async addPulse() {
			this.$v.addPulseForm.$touch()
			if (this.$v.addPulseForm.$invalid) {
				this.showNotification('warning', 'Please fill in all required fields')
				return
			}
			
			try {
				const result = await this.apiCall('POST', '/otx/pulses', this.addPulseForm)
				
				if (result.success) {
					this.showNotification('success', 'Pulse added successfully')
					this.showAddModal = false
					this.resetAddForm()
					this.loadPulses()
				} else {
					this.showNotification('error', result.error || 'Failed to add pulse')
				}
			} catch (error) {
				this.showNotification('error', 'Failed to add pulse')
			}
		},
		
		// Remove pulse
		async removePulse(pulse) {
			try {
				const result = await this.apiCall('DELETE', `/otx/pulses/${pulse.pulse_id}`)
				
				if (result.success) {
					this.showNotification('success', 'Pulse removed successfully')
					this.loadPulses()
				} else {
					this.showNotification('error', result.error || 'Failed to remove pulse')
				}
			} catch (error) {
				this.showNotification('error', 'Failed to remove pulse')
			}
		},
		
		// Toggle pulse status
		async togglePulseStatus(pulse) {
			const action = pulse.status === 'enabled' ? 'disable' : 'enable'
			
			try {
				const result = await this.apiCall('PUT', `/otx/pulses/${pulse.pulse_id}/${action}`)
				
				if (result.success) {
					this.showNotification('success', `Pulse ${action}d successfully`)
					this.loadPulses()
				} else {
					this.showNotification('error', result.error || `Failed to ${action} pulse`)
				}
			} catch (error) {
				this.showNotification('error', `Failed to ${action} pulse`)
			}
		},
		
		// Update pulse schedule
		async updateSchedule() {
			this.$v.scheduleForm.$touch()
			if (this.$v.scheduleForm.$invalid) {
				this.showNotification('warning', 'Please select a schedule interval')
				return
			}
			
			try {
				const result = await this.apiCall('PUT', `/otx/pulses/${this.selectedPulse.pulse_id}/schedule`, this.scheduleForm)
				
				if (result.success) {
					this.showNotification('success', 'Schedule updated successfully')
					this.showScheduleModal = false
					this.loadPulses()
				} else {
					this.showNotification('error', result.error || 'Failed to update schedule')
				}
			} catch (error) {
				this.showNotification('error', 'Failed to update schedule')
			}
		},
		
		
		// Modal and UI helpers
		openAddModal() {
			this.resetAddForm()
			this.showAddModal = true
		},
		
		openInfoModal(pulse) {
			this.selectedPulse = pulse
			this.showInfoModal = true
		},
		
		openScheduleModal(pulse) {
			this.selectedPulse = pulse
			this.scheduleForm.schedule_interval = pulse.schedule.interval
			this.showScheduleModal = true
		},
		
		openDeleteModal(pulse) {
			this.selectedPulse = pulse
			this.showDeleteModal = true
		},
		
		confirmDelete() {
			this.removePulse(this.selectedPulse)
			this.showDeleteModal = false
		},
		
		resetAddForm() {
			this.addPulseForm = {
				pulse_id: '',
				name: '',
				description: '',
				schedule_interval: '1 day',
				enabled: true
			}
			this.pulseInfo = null
			this.$v.$reset()
		},
		
		// Utility methods
		formatDate(dateString) {
			if (!dateString) return 'N/A'
			
			// Handle different date formats
			let date
			if (typeof dateString === 'string') {
				// ISO string format
				date = new Date(dateString)
			} else if (dateString && dateString.$date) {
				// MongoDB date format with $date
				date = new Date(dateString.$date)
			} else if (dateString instanceof Date) {
				// Already a Date object
				date = dateString
			} else {
				// Try to parse as-is
				date = new Date(dateString)
			}
			
			// Check if date is valid
			if (isNaN(date.getTime())) {
				return 'Invalid Date'
			}
			
			return date.toLocaleString()
		},
		
		getStatusBadgeVariant(status) {
			switch (status) {
				case 'enabled': return 'success'
				case 'disabled': return 'secondary'
				default: return 'secondary'
			}
		},
		
		showNotification(type, message) {
			const notification = { id: Date.now(), type, message }
			this.notifications.push(notification)
			
			setTimeout(() => {
				this.notifications = this.notifications.filter(n => n.id !== notification.id)
			}, 5000)
		},
		
		removeNotification(id) {
			this.notifications = this.notifications.filter(n => n.id !== id)
		},
		
		// Table event handlers
		onFiltered(filteredItems) {
			this.totalRows = filteredItems.length
			this.currentPage = 1
		},
		
		onPageChange() {
			this.loadPulses()
		},
		
		onStatusFilterChange() {
			this.currentPage = 1
			this.loadPulses()
		},
		
		// Watch for pulse ID changes in add form
		onPulseIdChange() {
			if (this.addPulseForm.pulse_id && this.addPulseForm.pulse_id.length > 3) {
				this.getPulseInfo(this.addPulseForm.pulse_id)
			} else {
				this.pulseInfo = null
			}
		}
	},
}
</script>

<template>
	<Layout>
		<PageHeader :title="title" :items="items" />
		
		<!-- Notifications -->
		<div v-if="notifications.length" class="fixed-top" style="top: 80px; right: 20px; z-index: 1050; max-width: 400px;">
			<div v-for="notification in notifications" :key="notification.id" 
				 :class="`alert alert-${notification.type === 'error' ? 'danger' : notification.type} alert-dismissible`" 
				 role="alert">
				{{ notification.message }}
				<button type="button" class="btn-close" @click="removeNotification(notification.id)"></button>
			</div>
		</div>
		
		<div class="row">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<!-- Tab Navigation -->
						<ul class="nav nav-tabs mb-4">
							<li class="nav-item">
								<a class="nav-link" :class="{ active: activeTab === 'list' }" 
								   @click="activeTab = 'list'" href="#">
									<feather type="list" class="icon-xs me-2"></feather>
									Manage Pulses
								</a>
							</li>
							<li class="nav-item">
								<a class="nav-link" :class="{ active: activeTab === 'search' }" 
								   @click="activeTab = 'search'" href="#">
									<feather type="search" class="icon-xs me-2"></feather>
									Search OTX
								</a>
							</li>
						</ul>
						
						<!-- Manage Pulses Tab -->
						<div v-if="activeTab === 'list'" class="tab-content">
							<!-- Controls -->
							<div class="row mb-3">
								<div class="col-md-3">
									<button @click="openAddModal" class="btn btn-primary">
										<feather type="plus" class="icon-xs me-2"></feather>
										Add Pulse
									</button>
								</div>
								<div class="col-md-3">
									<select v-model="statusFilter" @change="onStatusFilterChange" class="form-select">
										<option value="all">All Status</option>
										<option value="enabled">Enabled</option>
										<option value="disabled">Disabled</option>
									</select>
								</div>
								<div class="col-md-6">
									<div class="input-group">
										<input v-model="filter" type="search" placeholder="Search pulses..." 
											   class="form-control">
										<button @click="loadPulses" class="btn btn-outline-secondary">
											<feather type="refresh-cw" class="icon-xs"></feather>
										</button>
									</div>
								</div>
							</div>
							
							<!-- Statistics -->
							<div class="row mb-3">
								<div class="col-md-12">
									<div class="d-flex justify-content-between align-items-center">
										<span>
											Found: <strong>{{ totalRows }}</strong> pulses
										</span>
										<span class="text-muted">
											Last updated: {{ new Date().toLocaleString() }}
										</span>
									</div>
								</div>
							</div>
							
							<!-- Pulses Table -->
							<div class="table-responsive">
								<b-table
									:items="pulses"
									:fields="fields"
									:busy="isBusy"
									responsive="sm"
									:per-page="perPage"
									:current-page="currentPage"
									:sort-by.sync="sortBy"
									:sort-desc.sync="sortDesc"
									:filter="filter"
									:filter-included-fields="filterOn"
									@filtered="onFiltered"
								>
									<template v-slot:table-busy>
										<div class="text-center">
											<br><br>
											<b-spinner class="align-middle"></b-spinner>
											<strong>&nbsp;&nbsp;Loading...</strong>
										</div>
									</template>
									
									<template v-slot:cell(pulse_id)="data">
										<code class="text-primary">{{ data.item.pulse_id }}</code>
									</template>
									
									<template v-slot:cell(name)="data">
										<strong>{{ data.item.name || 'N/A' }}</strong>
										<br>
										<small class="text-muted">{{ data.item.description || '' }}</small>
									</template>
									
									<template v-slot:cell(status)="data">
										<span :class="`badge bg-${getStatusBadgeVariant(data.item.status)}`">
											{{ data.item.status }}
										</span>
									</template>
									
									<template v-slot:cell(schedule.interval)="data">
										<span class="badge bg-info">{{ (data.item.schedule && data.item.schedule.interval) || 'N/A' }}</span>
									</template>
									
									<template v-slot:cell(created_at)="data">
										{{ formatDate(data.item.created_at) }}
									</template>
									
									<template v-slot:cell(actions)="data">
										<div class="btn-group btn-group-sm">
											<button @click="openInfoModal(data.item)" class="btn btn-outline-info" title="View Details">
												<feather type="info" class="icon-xs"></feather>
											</button>
											<button @click="openScheduleModal(data.item)" class="btn btn-outline-warning" title="Update Schedule">
												<feather type="clock" class="icon-xs"></feather>
											</button>
											<button @click="togglePulseStatus(data.item)" 
													:class="`btn btn-outline-${data.item.status === 'enabled' ? 'danger' : 'success'}`"
													:title="data.item.status === 'enabled' ? 'Disable' : 'Enable'">
												<feather :type="data.item.status === 'enabled' ? 'pause' : 'play'" class="icon-xs"></feather>
											</button>
											<button @click="openDeleteModal(data.item)" class="btn btn-outline-danger" title="Remove">
												<feather type="trash-2" class="icon-xs"></feather>
											</button>
										</div>
									</template>
								</b-table>
							</div>
							
							<!-- Pagination -->
							<div class="row">
								<div class="col-sm-12 col-md-6">
									<div class="dataTables_length">
										<label class="d-inline-flex align-items-center">
											Show&nbsp;
											<select v-model="perPage" @change="onPageChange" class="form-select form-select-sm">
												<option v-for="option in pageOptions" :key="option" :value="option">
													{{ option }}
												</option>
											</select>
											&nbsp;entries
										</label>
									</div>
								</div>
								<div class="col-sm-12 col-md-6">
									<div class="dataTables_paginate paging_simple_numbers float-end">
										<b-pagination
											v-model="currentPage"
											:total-rows="totalRows"
											:per-page="perPage"
											@change="onPageChange"
											size="sm"
										></b-pagination>
									</div>
								</div>
							</div>
						</div>
						
						<!-- Search OTX Tab -->
						<div v-if="activeTab === 'search'" class="tab-content">
							<div class="row">
								<div class="col-md-8">
									<div class="input-group mb-3">
										<input v-model="searchQuery" @keyup.enter="searchPulses" 
											   type="text" placeholder="Search for pulses in OTX..." 
											   class="form-control">
										<button @click="searchPulses" :disabled="isSearching" class="btn btn-primary">
											<feather v-if="isSearching" type="loader" class="icon-xs spinning"></feather>
											<feather v-else type="search" class="icon-xs"></feather>
											{{ isSearching ? 'Searching...' : 'Search' }}
										</button>
									</div>
								</div>
							</div>
							
							<!-- Search Results -->
							<div v-if="searchResults && searchResults.length" class="row">
								<div class="col-12">
									<h5>Search Results ({{ searchResults.length }})</h5>
									<div class="row">
										<div v-for="result in searchResults" :key="result.id" class="col-md-6 col-lg-4 mb-3">
											<div class="card h-100">
												<div class="card-body">
													<h6 class="card-title">{{ result.name }}</h6>
													<p class="card-text">
														<small class="text-muted">ID: {{ result.id }}</small><br>
														<small class="text-muted">Author: {{ result.author_name }}</small><br>
														{{ result.description || 'No description available' }}
													</p>
													<div class="card-text">
														<small class="text-muted">
															Created: {{ formatDate(result.created) }}<br>
															Modified: {{ formatDate(result.modified) }}<br>
															Indicators: {{ result.indicator_count || 0 }}
														</small>
													</div>
													<div v-if="result.tags && result.tags.length" class="mb-2">
														<span v-for="tag in result.tags.slice(0, 3)" :key="tag" class="badge bg-secondary me-1">
															{{ tag }}
														</span>
														<span v-if="result.tags.length > 3" class="text-muted">
															+{{ result.tags.length - 3 }} more
														</span>
													</div>
												</div>
												<div class="card-footer">
													<button @click="addPulseForm.pulse_id = result.id; activeTab = 'list'; openAddModal()" 
															class="btn btn-sm btn-primary">
														Add to Collection
													</button>
													<span v-if="!result.public" class="badge bg-warning ms-2">Private</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							
							<div v-else-if="searchResults && searchResults.length === 0" class="text-center py-4">
								<p class="text-muted">No results found for your search.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		
		<!-- Add Pulse Modal -->
		<b-modal v-model="showAddModal" title="Add New Pulse" size="lg" @hidden="resetAddForm">
			<div class="row">
				<div class="col-md-6">
					<div class="mb-3">
						<label class="form-label">Pulse ID *</label>
						<input v-model="addPulseForm.pulse_id" @input="onPulseIdChange" 
							   type="text" class="form-control" 
							   :class="{ 'is-invalid': $v.addPulseForm.pulse_id.$error }"
							   placeholder="Enter OTX Pulse ID">
						<div v-if="$v.addPulseForm.pulse_id.$error" class="invalid-feedback">
							Pulse ID is required
						</div>
					</div>
					
					<div class="mb-3">
						<label class="form-label">Schedule Interval</label>
						<select v-model="addPulseForm.schedule_interval" class="form-select">
							<option v-for="interval in scheduleIntervals" :key="interval.value" :value="interval.value">
								{{ interval.text }}
							</option>
						</select>
					</div>
					
					<div class="mb-3">
						<div class="form-check">
							<input v-model="addPulseForm.enabled" type="checkbox" class="form-check-input" id="enabledCheck">
							<label class="form-check-label" for="enabledCheck">
								Enable immediately
							</label>
						</div>
					</div>
				</div>
				
				<div class="col-md-6">
					<!-- Pulse Info Preview -->
					<div v-if="isLoadingPulseInfo" class="text-center">
						<b-spinner class="align-middle"></b-spinner>
						<p class="mt-2">Loading pulse information...</p>
					</div>
					
					<div v-else-if="pulseInfo && pulseInfo.details" class="border rounded p-3">
						<h6>Pulse Information</h6>
						<p><strong>Name:</strong> {{ pulseInfo.details.name }}</p>
						<p><strong>Description:</strong> {{ pulseInfo.details.description || 'No description' }}</p>
						<p><strong>Indicators:</strong> {{ pulseInfo.indicator_count || 0 }}</p>
						<p><strong>Created:</strong> {{ formatDate(pulseInfo.details.created) }}</p>
					</div>
					
					<div v-else-if="addPulseForm.pulse_id" class="text-muted text-center p-3">
						Enter a valid Pulse ID to see information
					</div>
				</div>
			</div>
			
			<div class="row">
				<div class="col-12">
					<div class="mb-3">
						<label class="form-label">Name (Optional)</label>
						<input v-model="addPulseForm.name" type="text" class="form-control" placeholder="Pulse name">
					</div>
					
					<div class="mb-3">
						<label class="form-label">Description (Optional)</label>
						<textarea v-model="addPulseForm.description" class="form-control" rows="3" placeholder="Pulse description"></textarea>
					</div>
				</div>
			</div>
			
			<template #modal-footer>
				<button @click="showAddModal = false" class="btn btn-secondary">Cancel</button>
				<button @click="addPulse" class="btn btn-primary" :disabled="$v.addPulseForm.$invalid">Add Pulse</button>
			</template>
		</b-modal>
		
		<!-- Pulse Info Modal -->
		<b-modal v-model="showInfoModal" title="Pulse Details" size="lg">
			<div v-if="selectedPulse" class="row">
				<div class="col-12">
					<table class="table table-bordered">
						<tr>
							<th>Pulse ID</th>
							<td><code>{{ selectedPulse.pulse_id }}</code></td>
						</tr>
						<tr>
							<th>Name</th>
							<td>{{ selectedPulse.name || 'N/A' }}</td>
						</tr>
						<tr>
							<th>Description</th>
							<td>{{ selectedPulse.description || 'N/A' }}</td>
						</tr>
						<tr>
							<th>Status</th>
							<td>
								<span :class="`badge bg-${getStatusBadgeVariant(selectedPulse.status)}`">
									{{ selectedPulse.status }}
								</span>
							</td>
						</tr>
						<tr>
							<th>Schedule Interval</th>
							<td>{{ (selectedPulse.schedule && selectedPulse.schedule.interval) || 'N/A' }}</td>
						</tr>
						<tr>
							<th>Created At</th>
							<td>{{ formatDate(selectedPulse.created_at) }}</td>
						</tr>
						<tr>
							<th>Updated At</th>
							<td>{{ formatDate(selectedPulse.updated_at) }}</td>
						</tr>
						<tr>
							<th>Last Checked At</th>
							<td>{{ formatDate(selectedPulse.checked_at) }}</td>
						</tr>
						<tr>
							<th>Execution Count</th>
							<td>{{ selectedPulse.execution_count || 0 }}</td>
						</tr>
					</table>
					
					<!-- Pulse Indicators -->
					<div v-if="selectedPulse.pulse_info && selectedPulse.pulse_info.indicators" class="mt-3">
						<h6>Indicators ({{ selectedPulse.pulse_info.indicators.length }})</h6>
						<div class="table-responsive">
							<table class="table table-sm">
								<thead>
									<tr>
										<th>Type</th>
										<th>Indicator</th>
										<th>Description</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(indicator, index) in selectedPulse.pulse_info.indicators.slice(0, 10)" :key="index">
										<td><span class="badge bg-secondary">{{ indicator.type }}</span></td>
										<td><code>{{ indicator.indicator }}</code></td>
										<td>{{ indicator.description || 'N/A' }}</td>
									</tr>
								</tbody>
							</table>
							<p v-if="selectedPulse.pulse_info.indicators.length > 10" class="text-muted">
								... and {{ selectedPulse.pulse_info.indicators.length - 10 }} more indicators
							</p>
						</div>
					</div>
				</div>
			</div>
			
			<template #modal-footer>
				<button @click="showInfoModal = false" class="btn btn-secondary">Close</button>
			</template>
		</b-modal>
		
		<!-- Schedule Modal -->
		<b-modal v-model="showScheduleModal" title="Update Schedule">
			<div v-if="selectedPulse" class="mb-3">
				<label class="form-label">Schedule Interval</label>
				<select v-model="scheduleForm.schedule_interval" class="form-select"
						:class="{ 'is-invalid': $v.scheduleForm.schedule_interval.$error }">
					<option v-for="interval in scheduleIntervals" :key="interval.value" :value="interval.value">
						{{ interval.text }}
					</option>
				</select>
				<div v-if="$v.scheduleForm.schedule_interval.$error" class="invalid-feedback">
					Schedule interval is required
				</div>
			</div>
			
			<template #modal-footer>
				<button @click="showScheduleModal = false" class="btn btn-secondary">Cancel</button>
				<button @click="updateSchedule" class="btn btn-primary" :disabled="$v.scheduleForm.$invalid">Update Schedule</button>
			</template>
		</b-modal>
		
		<!-- Delete Confirmation Modal -->
		<b-modal v-model="showDeleteModal" title="Confirm Delete">
			<div v-if="selectedPulse">
				<p>Are you sure you want to remove the pulse <strong>{{ selectedPulse.name || selectedPulse.pulse_id }}</strong>?</p>
				<p class="text-danger">This action cannot be undone.</p>
			</div>
			
			<template #modal-footer>
				<button @click="showDeleteModal = false" class="btn btn-secondary">Cancel</button>
				<button @click="confirmDelete" class="btn btn-danger">Delete</button>
			</template>
		</b-modal>
	</Layout>
</template>

<style scoped>
.spinning {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}

.card-text small {
	line-height: 1.4;
}

.btn-group-sm .btn {
	margin-right: 2px;
}

.alert {
	margin-bottom: 0.5rem;
}

.nav-tabs .nav-link {
	cursor: pointer;
}

.table th {
	background-color: #f8f9fa;
	font-weight: 600;
}

.badge {
	font-size: 0.75em;
}

code {
	font-size: 0.875em;
}
</style>